import { useState, useMemo, useCallback, useEffect, type ReactNode } from 'react';
import type { EventWithCircuit } from '../api/eventsService';
import { EventContext, type EventContextType } from './eventContextTypes';

// Only store the event identifier in localStorage, never the full event data.
// This prevents stale embedded data (sessions, stations, etc.) from being served
// when the app loads — the full event is always fetched fresh from the API.
const STORAGE_KEY = 'fia-portal-selected-event-key';

// Legacy key used to store the full event object — clean it up on load
const LEGACY_STORAGE_KEY = 'fia-portal-selected-event';

/**
 * Read the stored event key from localStorage, handling legacy migration.
 * Called once on mount to get the initial stored key value.
 */
function getInitialStoredKey(): string | null {
  try {
    // Check for legacy full-object entry and migrate
    const legacyStored = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacyStored) {
      const existingKey = localStorage.getItem(STORAGE_KEY);
      if (!existingKey) {
        const parsed = JSON.parse(legacyStored) as EventWithCircuit;
        const key = `${parsed.season_year}-${parsed.circuit_id}-${parsed.start_date}`;
        localStorage.setItem(STORAGE_KEY, key);
      }
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    }
  } catch {
    // Ignore migration errors
  }

  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export const EventProvider = ({ children }: { children: ReactNode }) => {
  const [selectedEvent, setSelectedEventState] = useState<EventWithCircuit | null>(null);
  const [storedEventKey] = useState<string | null>(() => getInitialStoredKey());

  // Save only the event key to localStorage whenever event changes
  useEffect(() => {
    if (selectedEvent) {
      try {
        const key = `${selectedEvent.season_year}-${selectedEvent.circuit_id}-${selectedEvent.start_date}`;
        localStorage.setItem(STORAGE_KEY, key);
      } catch (error) {
        console.error('Error saving event key to localStorage:', error);
      }
    }
  }, [selectedEvent]);

  // Memoize the setter function to prevent it from changing on every render
  const setSelectedEvent = useCallback((event: EventWithCircuit | null) => {
    setSelectedEventState(event);
  }, []);

  const value: EventContextType = useMemo(
    () => ({
      selectedEvent,
      setSelectedEvent,
      storedEventKey,
    }),
    [selectedEvent, setSelectedEvent, storedEventKey]
  );

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
};

